import React, { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";

/**
 * Small inline controls added to a reply form after "Change status":
 *   - Assign to: user dropdown limited to the logged-in user's own team members
 *     (lead + members of any team they belong to). Self-assignment allowed.
 *   - Forward to: dropdown of all teams; selecting one re-routes the item
 *     (the backend will clear the existing assignee so the new team's lead picks it up).
 *
 * Props:
 *  - assignTo, setAssignTo
 *  - forwardTo, setForwardTo
 *  - referenceData (from useRefData) — provides teams (with members) and users
 *  - kind: "task" | "ticket"
 */
export default function ReplyAssignControls({ assignTo, setAssignTo, forwardTo, setForwardTo, referenceData, kind, forwardEnabled = true }) {
  const [myTeamIds, setMyTeamIds] = useState([]);

  useEffect(() => {
    let aborted = false;
    api.get("/me/modules")
      .then(({ data }) => { if (!aborted) setMyTeamIds(data?.team_ids || []); })
      .catch(() => {});
    return () => { aborted = true; };
  }, []);

  const allTeams = referenceData?.teams || [];
  const myTeams = allTeams.filter((t) => myTeamIds.includes(t.value));
  const teamMemberIds = new Set(
    myTeams.flatMap((t) => [t.team_lead_id, ...(t.members || [])].filter(Boolean))
  );
  const candidates = (referenceData?.users || []).filter((u) => teamMemberIds.has(u.value));

  const tid = (suffix) => `${kind}-reply-${suffix}`;

  return (
    <>
      <div className="flex items-center gap-2">
        <Label className="text-xs text-zinc-400">Assign to</Label>
        <Select value={assignTo} onValueChange={setAssignTo}>
          <SelectTrigger className="bg-zinc-950 border-white/10 text-white w-44" data-testid={tid("assign-to")}>
            <SelectValue placeholder="No change" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
            {candidates.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-500">No team members found</div>
            ) : candidates.map((u) => (
              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {assignTo && (
          <button type="button" onClick={() => setAssignTo("")} className="text-xs text-zinc-500 hover:text-white">
            Clear
          </button>
        )}
      </div>

      {forwardEnabled && (
      <div className="flex items-center gap-2">
        <Label className="text-xs text-zinc-400">Forward to</Label>
        <Select value={forwardTo} onValueChange={setForwardTo}>
          <SelectTrigger className="bg-zinc-950 border-white/10 text-white w-44" data-testid={tid("forward-to")}>
            <SelectValue placeholder="No change" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-zinc-100">
            {allTeams.length === 0 ? (
              <div className="px-2 py-3 text-xs text-zinc-500">No teams found</div>
            ) : allTeams.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {forwardTo && (
          <button type="button" onClick={() => setForwardTo("")} className="text-xs text-zinc-500 hover:text-white">
            Clear
          </button>
        )}
      </div>
      )}
    </>
  );
}
